// ###### IMPORTS
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../connections/api';
import { loadFromCacheByEndpoint } from '../offline_items/offlineDataLoader';
import { dataFetchTracker } from './DataFetchTracker';

function readListMetaLocalStorage(listMetaLocalStorageKey) {
  if (!listMetaLocalStorageKey || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(listMetaLocalStorageKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// ###### CUSTOM HOOK
// Offline-first data loading hook
// Loads data from cache immediately, then syncs with server in background
export const useOfflineFirstData = (endpoint, options = {}) => {
  const {
    params = {},
    enabled = true,
    refreshInterval = null,
    onDataChange = null,
    /** `(rawBody) => { records?: T[], meta?: object }` — used for `{ results, window }`-style payloads */
    parseListPayload = null,
    listMetaLocalStorageKey = null,
  } = options;

  const paramsStable = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const [data, setData] = useState(null);
  const [listMeta, setListMeta] = useState(() => readListMetaLocalStorage(listMetaLocalStorageKey));
  const [isLoadingCache, setIsLoadingCache] = useState(true);
  const [isLoadingNetwork, setIsLoadingNetwork] = useState(false);
  const [error, setError] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [hasTriedNetwork, setHasTriedNetwork] = useState(false);

  const refreshIntervalRef = useRef(null);
  const isMountedRef = useRef(true);
  const dataRef = useRef(null);
  const listMetaLocalStorageKeyRef = useRef(listMetaLocalStorageKey);
  const parseListPayloadRef = useRef(parseListPayload);
  const onDataChangeRef = useRef(onDataChange);
  const paramsRef = useRef(params);
  const fetchingRef = useRef(false);

  useEffect(() => {
    listMetaLocalStorageKeyRef.current = listMetaLocalStorageKey;
  }, [listMetaLocalStorageKey]);

  useEffect(() => {
    parseListPayloadRef.current = parseListPayload;
  }, [parseListPayload]);

  // Update refs when values change
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
    paramsRef.current = params;
  }, [onDataChange, params]);

  // ##### LOAD FROM CACHE
  const loadFromCache = useCallback(async () => {
    if (!enabled) {
      setIsLoadingCache(false);
      return;
    }

    setIsLoadingCache(true);
    setError(null);

    try {
      const cachedData = await loadFromCacheByEndpoint(endpoint, paramsRef.current);

      if (isMountedRef.current) {
        let cacheResults;
        if (parseListPayloadRef.current) {
          const parsed =
            parseListPayloadRef.current(
              Array.isArray(cachedData) ? { results: cachedData } : cachedData,
            ) || {};
          cacheResults = parsed.records;
        } else {
          cacheResults = Array.isArray(cachedData) ? cachedData : cachedData.results || cachedData;
        }
        if (!Array.isArray(cacheResults)) {
          cacheResults = [];
        }
        setData(cacheResults);
        dataRef.current = cacheResults;
        const key = listMetaLocalStorageKeyRef.current;
        if (key && isMountedRef.current) {
          setListMeta(readListMetaLocalStorage(key));
        }

        setIsLoadingCache(false);

        // If cache is empty and offline, mark as tried (no point waiting for network)
        if (cacheResults.length === 0 && !navigator.onLine) {
          setHasTriedNetwork(true);
        }
      }
    } catch (err) {
      console.error('[useOfflineFirstData] Failed to load from cache:', err);
      if (isMountedRef.current) {
        setData([]);
        dataRef.current = [];
        setError(err.message);
        setIsLoadingCache(false);

        if (!navigator.onLine) {
          setHasTriedNetwork(true);
        }
      }
    }
  }, [endpoint, enabled]);

  // ##### FETCH FROM NETWORK
  const fetchFromNetwork = useCallback(
    async (silent = false) => {
      if (!enabled || !navigator.onLine) {
        if (isMountedRef.current) {
          setHasTriedNetwork(true);
        }
        return;
      }

      if (fetchingRef.current) {
        return;
      }

      fetchingRef.current = true;

      dataFetchTracker.startFetch(endpoint);

      if (!silent) {
        setIsLoadingNetwork(true);
      }
      setError(null);

      try {
        const response = await api.get(endpoint, { params: paramsRef.current });
        const raw = response.data;

        if (isMountedRef.current) {
          setHasTriedNetwork(true);

          let newData;

          const parser = parseListPayloadRef.current;
          const metaKey = listMetaLocalStorageKeyRef.current;

          if (parser) {
            const parsed = parser(raw) || {};
            newData = Array.isArray(parsed.records) ? parsed.records : [];
            if (parsed.meta != null) {
              if (metaKey) {
                try {
                  localStorage.setItem(metaKey, JSON.stringify(parsed.meta));
                } catch {
                  // ignore quota / private mode
                }
              }
              setListMeta(parsed.meta);
            }
          } else {
            newData = raw?.results || raw || [];
            if (!Array.isArray(newData)) {
              newData = [];
            }
          }

          const currentData = dataRef.current;
          const dataChanged = JSON.stringify(currentData) !== JSON.stringify(newData);

          if (dataChanged) {
            setData(newData);
            dataRef.current = newData;

            if (onDataChangeRef.current) {
              onDataChangeRef.current(newData);
            }
          }

          setLastSyncTime(new Date().toISOString());

          setIsLoadingNetwork(false);
        }
      } catch (err) {
        console.error('Failed to fetch from network:', err);
        if (isMountedRef.current) {
          setHasTriedNetwork(true);

          if (!dataRef.current) {
            setError(err.message);
          }
          setIsLoadingNetwork(false);
        }
      } finally {
        dataFetchTracker.endFetch(endpoint);
        fetchingRef.current = false;
      }
    },
    [endpoint, enabled],
  );

  // ##### REFRESH DATA
  const refresh = useCallback(async () => {
    await fetchFromNetwork(false);
  }, [fetchFromNetwork]);

  // ##### OPTIMISTIC UPDATE
  const updateOptimistically = useCallback((updatedItem) => {
    if (!updatedItem || !updatedItem.id) return;

    if (isMountedRef.current && dataRef.current) {
      const currentData = Array.isArray(dataRef.current) ? dataRef.current : [];

      const existingIndex = currentData.findIndex((item) => item.id === updatedItem.id);

      let updatedData;
      if (existingIndex >= 0) {
        updatedData = currentData.map((item) =>
          item.id === updatedItem.id ? { ...item, ...updatedItem } : item,
        );
      } else {
        updatedData = [...currentData, updatedItem];
      }

      setData(updatedData);
      dataRef.current = updatedData;
    }
  }, []);

  const loadFromCacheRef = useRef(loadFromCache);
  const fetchFromNetworkRef = useRef(fetchFromNetwork);

  useEffect(() => {
    loadFromCacheRef.current = loadFromCache;
    fetchFromNetworkRef.current = fetchFromNetwork;
  }, [loadFromCache, fetchFromNetwork]);

  // ##### INITIAL LOAD
  useEffect(() => {
    isMountedRef.current = true;

    loadFromCacheRef.current();

    if (navigator.onLine) {
      const timeoutId = setTimeout(() => {
        fetchFromNetworkRef.current(true);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        isMountedRef.current = false;
      };
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [endpoint, paramsStable, enabled]);

  useEffect(() => {
    if (!listMetaLocalStorageKey) {
      setListMeta(null);
      return;
    }
    setListMeta(readListMetaLocalStorage(listMetaLocalStorageKey));
  }, [endpoint, listMetaLocalStorageKey]);

  // ##### REFRESH INTERVAL
  useEffect(() => {
    if (refreshInterval && navigator.onLine) {
      refreshIntervalRef.current = setInterval(() => {
        fetchFromNetworkRef.current(true);
      }, refreshInterval);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [refreshInterval]);

  // ##### ONLINE/OFFLINE LISTENERS
  useEffect(() => {
    const handleOnline = () => {
      fetchFromNetworkRef.current(true);
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  const isLoading =
    (isLoadingCache && data === null) ||
    (data !== null && Array.isArray(data) && data.length === 0 && !hasTriedNetwork && navigator.onLine);

  return {
    data,
    listMeta,
    isLoading,
    isLoadingCache,
    isLoadingNetwork,
    error,
    lastSyncTime,
    refresh,
    updateOptimistically,
  };
};

export default useOfflineFirstData;
